import { Link } from "react-router-dom";
import { getNeighbors, CITY_LABELS } from "@/config/city-neighbors";

interface CrossLinksFooterProps {
  serviceSlug: string; // "persianas", "toldos", "mosquiteras"
  cityName: string;
  currentSlug: string;
}

const TIER1_CITIES = [
  { label: "Málaga", slug: "malaga" },
  { label: "Marbella", slug: "marbella" },
  { label: "Fuengirola", slug: "fuengirola" },
  { label: "Torremolinos", slug: "torremolinos" },
  { label: "Benalmádena", slug: "benalmadena" },
  { label: "Mijas", slug: "mijas" },
  { label: "Estepona", slug: "estepona" },
  { label: "Vélez-Málaga", slug: "velez-malaga" },
  { label: "Rincón de la Victoria", slug: "rincon-de-la-victoria" },
];

const TIER2_CITIES = [
  { label: "Antequera", slug: "antequera" },
  { label: "Ronda", slug: "ronda" },
  { label: "Nerja", slug: "nerja" },
  { label: "Alhaurín de la Torre", slug: "alhaurin-de-la-torre" },
  { label: "Alhaurín el Grande", slug: "alhaurin-el-grande" },
  { label: "Coín", slug: "coin" },
  { label: "Cártama", slug: "cartama" },
  { label: "Álora", slug: "alora" },
];

const REGION_HUBS = [
  { label: "Axarquía", slug: "axarquia" },
  { label: "Valle del Guadalhorce", slug: "valle-del-guadalhorce" },
  { label: "Serranía de Ronda", slug: "serrania-de-ronda" },
];

export default function CrossLinksFooter({
  serviceSlug,
  cityName,
  currentSlug,
}: CrossLinksFooterProps) {
  const neighbors = getNeighbors(currentSlug);

  // Si cette ville a des voisins définis, afficher uniquement ceux-ci (max 4)
  // Sinon, fallback au listing classique 9 Tier 1 + 3 hubs
  const hasGeoNeighbors = neighbors.length > 0;

  const serviceLabel =
    serviceSlug === "persianas"
      ? "persianas"
      : serviceSlug === "toldos"
        ? "toldos"
        : "mosquiteras";

  if (hasGeoNeighbors) {
    return (
      <section className="bg-surface-alt py-10 md:py-14 border-t border-border/30">
        <div className="max-w-container mx-auto px-4 md:px-6">
          <h2 className="font-heading font-bold text-lg text-text-primary mb-2">
            También reparamos {serviceLabel} cerca de {cityName}
          </h2>
          <p className="font-body text-sm text-text-secondary mb-5 max-w-xl">
            Visitas agrupadas a pueblos vecinos para reducir costes de
            desplazamiento.
          </p>
          <div className="flex flex-wrap gap-3">
            {neighbors.slice(0, 4).map((n) => (
              <Link
                key={n.slug}
                to={`/${serviceSlug}/${n.slug}`}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 border border-border rounded-pill font-body text-sm text-text-secondary hover:bg-white hover:text-primary hover:border-primary transition-colors whitespace-nowrap"
              >
                <i className="ri-map-pin-line text-xs" aria-hidden="true"></i>
                {n.label}
              </Link>
            ))}
          </div>
          <div className="mt-6 pt-6 border-t border-border/30">
            <p className="font-body text-xs text-text-secondary">
              No encuentras tu pueblo? Contáctanos y te confirmamos
              disponibilidad. También cubrimos{" "}
              <Link
                to="/zonas"
                className="text-primary hover:underline font-medium"
              >
                todas las zonas
              </Link>{" "}
              de la provincia.
            </p>
          </div>
        </div>
      </section>
    );
  }

  // Fallback: page non-city ou sans voisins mappés -> afficher la structure classique
  return (
    <section className="bg-surface-alt py-10 md:py-14 border-t border-border/30">
      <div className="max-w-container mx-auto px-4 md:px-6">
        <h2 className="font-heading font-bold text-lg text-text-primary mb-4 text-center">
          También reparamos {serviceLabel} cerca de {cityName}
        </h2>
        <p className="text-center font-body text-sm text-text-secondary mb-6">
          Nos desplazamos a las localidades vecinas de forma regular. Si no
          encuentras tu pueblo, contáctanos.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Costa principal */}
          <div>
            <h3 className="font-heading font-bold text-sm text-primary uppercase tracking-wide mb-3">
              Costa del Sol
            </h3>
            <div className="flex flex-wrap gap-x-2 gap-y-2 text-sm font-body text-text-secondary leading-relaxed">
              {TIER1_CITIES.filter((c) => c.slug !== currentSlug).map(
                (city, i, arr) => (
                  <span key={city.slug} className="flex items-center">
                    <Link
                      to={`/${serviceSlug}/${city.slug}`}
                      className="hover:text-primary transition-colors whitespace-nowrap"
                    >
                      {city.label}
                    </Link>
                    {i < arr.length - 1 && (
                      <span className="text-border mx-1.5 select-none">·</span>
                    )}
                  </span>
                )
              )}
            </div>
          </div>

          {/* Interior cercano */}
          <div>
            <h3 className="font-heading font-bold text-sm text-primary uppercase tracking-wide mb-3">
              Interior y Sierra
            </h3>
            <div className="flex flex-wrap gap-x-2 gap-y-2 text-sm font-body text-text-secondary leading-relaxed">
              {TIER2_CITIES.filter((c) => c.slug !== currentSlug).map(
                (city, i, arr) => (
                  <span key={city.slug} className="flex items-center">
                    <Link
                      to={`/${serviceSlug}/${city.slug}`}
                      className="hover:text-primary transition-colors whitespace-nowrap"
                    >
                      {city.label}
                    </Link>
                    {i < arr.length - 1 && (
                      <span className="text-border mx-1.5 select-none">·</span>
                    )}
                  </span>
                )
              )}
            </div>
          </div>

          {/* Comarcas rurales */}
          <div>
            <h3 className="font-heading font-bold text-sm text-primary uppercase tracking-wide mb-3">
              Comarcas Rurales
            </h3>
            <div className="flex flex-wrap gap-x-2 gap-y-2 text-sm font-body text-text-secondary leading-relaxed">
              {REGION_HUBS.map((hub, i, arr) => (
                <span key={hub.slug} className="flex items-center">
                  <Link
                    to={`/zonas/${hub.slug}`}
                    className="hover:text-primary transition-colors whitespace-nowrap"
                  >
                    {hub.label}
                  </Link>
                  {i < arr.length - 1 && (
                    <span className="text-border mx-1.5 select-none">·</span>
                  )}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}